import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Save, Undo2, Loader2, X, Trash2, Scale,
} from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { PERIODS, periodLabel } from '../../utils/financialPeriods';
import { journalService } from '../../services/journalService';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import usePeriodDrilldown, { periodColumns, periodsOfCol, periodHeaderToggle, aggPeriods } from '../../hooks/usePeriodDrilldown';
import PickerPopover from '../../components/PickerPopover';
import SearchSelect from '../../components/SearchSelect';

// =============================================================================
// Finance › Journals
//
// Top-side adjustments: what finance overlays on top of what the functions
// submitted. Every group budget has them — a restructuring provision the sites
// know nothing about, a central rebate, a head-office recharge.
//
// THE ONE DESIGN RULE THIS PAGE EXISTS TO PROTECT:
//
//     A JOURNAL NEVER WRITES INTO MODULE DATA.
//
// It is a separate layer — its own tables, its own branch in
// vw_bud_gl_consolidated — and the P&L is always "submitted + journals", always
// separable. The moment a journal edits a module's number, the question "what
// did the business submit versus what did finance overlay" becomes permanently
// unanswerable, and that question is the entire reason top-side adjustments
// exist as a concept. Every finance system that has ever got this wrong got it
// wrong by being helpful.
//
// NO POSTING, NO STATUS, NO APPROVAL (Keith, 17 Aug 2026: "this is not an ERP,
// it's a budget tool — we save lines that can be deleted"). Save puts the
// adjustment on the P&L; delete takes it off. That is the whole model, and it
// is the same one as every other capture screen in this product. DRAFT/POSTED
// was ERP thinking carried over by habit: posting means something in a general
// ledger because a posted entry is a permanent financial record, and nothing
// here is one.
//
// THE FOOT ROW IS INFORMATION, NOT A GATE. It shows the net effect per month,
// which is worth seeing — a two-sided journal that fails to net to zero in some
// month is usually a typo. But a ONE-SIDED adjustment ("add R4.2m of
// restructuring cost", no contra entry) is completely normal in a budget and
// nonsense in a ledger, so nothing on this page refuses it.
//
// SITE-LEVEL AND GROUP-LEVEL ARE BOTH ALLOWED (Keith, same day). Site empty = a
// group-level adjustment belonging to no depot, which is most of them. Name a
// site and the adjustment shows on that site's own P&L — which is the point of
// being able to.
//
// SIGN: DEBIT-POSITIVE, exactly like the consolidated ledger. A cost is
// positive, revenue or a credit is negative, and a balanced journal sums to
// zero. Nothing on this page flips anything; the P&L flips once, server-side.
// =============================================================================

const btn = 'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors';
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors';

const MUTED = { textDark: '#94a3b8' };
const MISSING = { textDark: '#dc2626' };
const CREDIT = { textDark: '#059669' };
const T_TOTAL = { textDark: '#0f172a', bgCell: '#f1f5f9' };

const fmtR = (n) => `R ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtA = (n) => (Number(n) === 0 ? '' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }));

const emptyPeriods = () => Object.fromEntries(PERIODS.map((p) => [p, 0]));
const lineTotal = (r) => PERIODS.reduce((a, p) => a + (Number(r.periods?.[p]) || 0), 0);

let draftSeq = 0;

export default function Journals() {
  const navigate = useNavigate();
  const { selectedVersion, fyStartMonth } = useAppContext();

  const [list, setList] = useState([]);
  const [picks, setPicks] = useState({ accounts: [], costCenters: [], sites: [] });
  const [selectedId, setSelectedId] = useState(null);
  const [journal, setJournal] = useState(null);
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [picker, setPicker] = useState(null);
  // null = not creating; a string = the description being typed for a new one.
  const [creating, setCreating] = useState(null);

  const rowsRef = useRef([]);
  rowsRef.current = rows;

  // ---- Load ------------------------------------------------------------------
  const loadList = useCallback(async (keepId) => {
    setError('');
    try {
      const res = await journalService.list();
      const js = res.data?.journals || [];
      setList(js);
      setPicks(res.data?.picks || { accounts: [], costCenters: [], sites: [] });
      setSelectedId((cur) => {
        const want = keepId || cur;
        return js.some((j) => j.journal_id === want) ? want : (js[0]?.journal_id || null);
      });
      return js;
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load journals.');
      return [];
    }
  }, []);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setJournal(null); setRows([]); return; }
    try {
      const res = await journalService.get(id);
      setJournal(res.data?.journal || null);
      setRows((res.data?.lines || []).map((l) => ({
        __key: l.journal_line_id,
        journal_line_id: l.journal_line_id,
        account_code: l.account_code || '',
        cost_center_code: l.cost_center_code || '',
        site_code: l.site_code || '',
        description: l.description || '',
        periods: { ...emptyPeriods(), ...(l.periods || {}) },
      })));
      setDirty(false);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load that journal.');
    }
  }, []);

  useEffect(() => {
    if (!selectedVersion) return;
    setLoading(true);
    loadList().finally(() => setLoading(false));
  }, [loadList, selectedVersion]);

  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  // ---- Labels ----------------------------------------------------------------
  const label = useCallback((arr, codeKey, nameKey, code, fallback) => {
    if (!code) return fallback;
    const hit = (arr || []).find((x) => x[codeKey] === code);
    return hit ? `${code} · ${hit[nameKey]}` : code;
  }, []);
  const accLabel = useCallback((c) => label(picks.accounts, 'account_code', 'account_description', c, ''), [picks.accounts, label]);
  const ccLabel = useCallback((c) => label(picks.costCenters, 'cost_center_code', 'description', c, 'none'), [picks.costCenters, label]);
  const siteLabel = useCallback((c) => label(picks.sites, 'site_code', 'site_name', c, 'Group'), [picks.sites, label]);

  // The selector's options. Everything the old left-hand list showed in a row
  // still travels — the reference, what it is for, its value, and
  // whether it balances — because an unbalanced journal in a list of thirty is
  // exactly the thing nobody goes looking for. SearchSelect matches on code AND
  // name, so "JNL-002" and "recharge" both find it.
  //
  // `label`, NOT `name`: SearchSelect renders `code · name` by default, and this
  // option's code is a uuid — machinery the user should never read. `label`
  // overrides that and is still searched.
  const journalOptions = useMemo(() => list.map((j) => ({
    code: j.journal_id,
    label: `${j.journal_ref} · ${j.description || '(no description)'}`,
    hint: [fmtR(j.value), j.is_balanced ? null : 'nets non-zero'].filter(Boolean).join(' · '),
  })), [list]);

  // ---- The per-period balance, computed on the working copy -------------------
  // Live, not on save: an out-of-balance period you can see while typing is a
  // typo, and one you discover on Post is an afternoon.
  const periodTotals = useMemo(
    () => Object.fromEntries(PERIODS.map((p) => [p, rows.reduce((a, r) => a + (Number(r.periods?.[p]) || 0), 0)])),
    [rows]
  );
  const outOfBalance = useMemo(
    () => PERIODS.filter((p) => Math.round((periodTotals[p] || 0) * 100) !== 0),
    [periodTotals]
  );
  const grandTotal = useMemo(() => PERIODS.reduce((a, p) => a + (periodTotals[p] || 0), 0), [periodTotals]);
  const journalValue = useMemo(
    () => rows.reduce((a, r) => a + PERIODS.reduce((b, p) => b + Math.max(Number(r.periods?.[p]) || 0, 0), 0), 0),
    [rows]
  );

  // ---- Grid ------------------------------------------------------------------
  // PERIODS DRILL DOWN, same as OPEX entry and the P&L: Full Year → quarters →
  // months. A journal is usually entered as one annual figure and read as one
  // annual figure — the twelve columns are for the months you actually vary.
  // Aggregate columns are read-only: there is no sane way to type into "Q2".
  const drill = usePeriodDrilldown(PERIODS);

  const gcols = useMemo(() => ([
    { id: 'account_code', title: 'Account', width: 210, grow: 1 },
    { id: 'description', title: 'Description', width: 170, grow: 1 },
    { id: 'cost_center_code', title: 'Cost Centre', width: 120 },
    { id: 'site_code', title: 'Site', width: 120 },
    ...periodColumns(drill, fyStartMonth, { monthWidth: 82, groupWidth: 118 }),
    { id: 'total', title: 'Total', width: 104 },
    { id: 'del', title: '', width: 36 },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [fyStartMonth, drill.visibleCols, drill.yearExpanded, drill.expandedQuarters]);
  const colIds = useMemo(() => gcols.map((c) => c.id), [gcols]);

  const getCellContent = useCallback(([col, row]) => {
    const id = colIds[col];

    // ---- The NET EFFECT row -------------------------------------------------
    // What this journal does to the P&L, month by month. NOT a balance check
    // and nothing is red: a one-sided adjustment is a perfectly good budget
    // entry, and it nets to itself.
    if (row === rows.length) {
      if (id === 'account_code') {
        return gcell.text('NET EFFECT', { readonly: true, theme: T_TOTAL });
      }
      const ps = periodsOfCol(gcols, id);
      if (ps) {
        const v = aggPeriods(ps, periodTotals);
        return gcell.number(v, { readonly: true, display: fmtA(v) || '—', theme: T_TOTAL });
      }
      if (id === 'total') {
        return gcell.number(grandTotal, { readonly: true, display: fmtA(grandTotal) || '—', theme: T_TOTAL });
      }
      return gcell.text('', { readonly: true, theme: T_TOTAL });
    }

    const r = rows[row];
    if (!r) return gcell.text('', { readonly: true });

    switch (id) {
      // No account means the line posts nowhere and vanishes from the statement
      // without an error, so it reads as broken rather than blank.
      case 'account_code':
        return gcell.text(accLabel(r.account_code) || '— pick an account —',
          { readonly: true, theme: r.account_code ? undefined : MISSING });
      case 'description':
        return gcell.text(r.description || '');
      case 'cost_center_code':
        return gcell.text(ccLabel(r.cost_center_code), { readonly: true, theme: r.cost_center_code ? undefined : MUTED });
      // Empty site = a group-level adjustment belonging to no depot, which is
      // the normal case — so it reads "Group", not as something missing.
      case 'site_code':
        return gcell.text(siteLabel(r.site_code), { readonly: true, theme: r.site_code ? undefined : MUTED });
      case 'total': {
        const t = lineTotal(r);
        return gcell.number(t, { display: fmtA(t) || '0', theme: t < 0 ? CREDIT : undefined });
      }
      case 'del':
        return gcell.text('✕', { readonly: true, theme: MUTED });
      default: {
        const ps = periodsOfCol(gcols, id);
        if (!ps) return gcell.text('', { readonly: true });
        const v = aggPeriods(ps, r.periods);
        // One period = the month cell you type into. Several = a quarter or the
        // year, which is an aggregate and read-only.
        return gcell.number(v, {
          readonly: ps.length > 1,
          display: fmtA(v),
          theme: v < 0 ? CREDIT : (ps.length > 1 ? MUTED : undefined),
        });
      }
    }
  }, [colIds, gcols, rows, accLabel, ccLabel, siteLabel, periodTotals, grandTotal]);

  // Typing a TOTAL spreads it evenly across the twelve periods. This is the
  // whole reason the column is editable: most top-side adjustments are "R4.2m,
  // even across the year", and typing twelve boxes for that is exactly the
  // friction that keeps the adjustment in a spreadsheet instead. The rounding
  // remainder goes into the last period so the twelve still sum to the total —
  // a spread that quietly loses 4c would be worse than no spread at all.
  const spreadEven = (total) => {
    const per = Math.round((total / 12) * 100) / 100;
    const out = Object.fromEntries(PERIODS.map((p) => [p, per]));
    out[12] = Math.round((total - per * 11) * 100) / 100;
    return out;
  };

  const onCellEdited = useCallback(([col, row], newVal) => {
    if (row >= rowsRef.current.length) return; // balance row
    const id = colIds[col];
    setRows((prev) => prev.map((r, i) => {
      if (i !== row) return r;
      if (id === 'description') return { ...r, description: String(newVal.data ?? '') };
      if (id === 'total') return { ...r, periods: spreadEven(Number(newVal.data) || 0) };
      const ps = periodsOfCol(gcols, id);
      // Only a single-month column is editable; Q and Full Year are aggregates.
      if (ps?.length === 1) {
        return { ...r, periods: { ...r.periods, [ps[0]]: Number(newVal.data) || 0 } };
      }
      return r;
    }));
    setDirty(true);
  }, [colIds, gcols]);

  const onCellClicked = useCallback(([col, row], event) => {
    if (row >= rowsRef.current.length) return;
    const id = colIds[col];
    const b = event?.bounds;
    const pos = { x: b ? b.x : 200, y: b ? b.y + b.height : 200 };
    if (id === 'del') {
      setRows((prev) => prev.filter((_, i) => i !== row));
      setDirty(true);
      return;
    }
    if (['account_code', 'cost_center_code', 'site_code'].includes(id)) {
      setPicker({ row, field: id, ...pos });
    }
  }, [colIds]);

  const pickerOptions = useMemo(() => {
    if (!picker) return [];
    switch (picker.field) {
      // Only accounts the chart of accounts allows journals against — the
      // server filters on allow_manual_journals, so control accounts are simply
      // not on the list rather than offered and refused on save.
      case 'account_code':
        return (picks.accounts || []).map((a) => ({
          code: a.account_code,
          description: a.account_description,
          hint: [a.account_group, a.pnl_section].filter(Boolean).join(' · '),
        }));
      case 'cost_center_code':
        return (picks.costCenters || []).map((c) => ({ code: c.cost_center_code, description: c.description }));
      case 'site_code':
        return (picks.sites || []).map((s) => ({ code: s.site_code, description: s.site_name }));
      default: return [];
    }
  }, [picker, picks]);

  const applyPick = (code) => {
    setRows((prev) => prev.map((r, i) => (i === picker.row ? { ...r, [picker.field]: code || '' } : r)));
    setDirty(true);
    setPicker(null);
  };

  // ---- Actions ---------------------------------------------------------------
  const addLine = () => {
    draftSeq += 1;
    setRows((prev) => [...prev, {
      __key: `d${draftSeq}`,
      journal_line_id: null,
      account_code: '',
      cost_center_code: '',
      site_code: '',
      description: '',
      periods: emptyPeriods(),
    }]);
    setDirty(true);
  };

  // A journal is CREATED WITH ITS DESCRIPTION, not named afterwards. The
  // reference is allocated by the server and says nothing — JNL-003 is not a
  // restructuring provision — so a journal born without a description is one
  // nobody can identify in the selector, and "I'll fill it in later" is how a
  // list of six untitled adjustments happens.
  const newJournal = async () => {
    const desc = (creating || '').trim();
    if (!desc) return;
    setBusy(true); setError('');
    try {
      const res = await journalService.create(desc);
      const id = res.data?.journal_id;
      setCreating(null);
      await loadList(id);
      setSelectedId(id);
      setToast(res.message || 'Journal created.');
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!journal) return;
    const missing = rows.findIndex((r) => !r.account_code);
    if (missing >= 0) {
      setError(`Line ${missing + 1} has no GL account. A line with no account posts nowhere and simply disappears from the statement.`);
      return;
    }
    setBusy(true); setError('');
    try {
      const res = await journalService.saveLines(journal.journal_id, rows.map((r) => ({
        journal_line_id: r.journal_line_id || null,
        account_code: r.account_code,
        cost_center_code: r.cost_center_code || null,
        site_code: r.site_code || null,
        description: r.description || null,
        periods: r.periods,
      })));
      setToast(res.message || 'Saved.');
      await loadDetail(journal.journal_id);
      await loadList(journal.journal_id);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not save the journal.');
    } finally { setBusy(false); }
  };

  const removeJournal = async () => {
    if (!journal) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${journal.journal_ref}? It is on the P&L now, so the statement drops the adjustment immediately. This cannot be undone.`)) return;
    setBusy(true); setError('');
    try {
      const res = await journalService.remove(journal.journal_id);
      setToast(res.message);
      setSelectedId(null);
      await loadList(null);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally { setBusy(false); }
  };

  const saveHeader = async (patch) => {
    if (!journal) return;
    try {
      await journalService.updateHeader(journal.journal_id, patch);
      await loadList(journal.journal_id);
      setJournal((j) => ({ ...j, ...patch }));
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-3">
        <button className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                onClick={() => navigate('/finance')}>
          <ArrowLeft className="w-3.5 h-3.5" /> Finance
        </button>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Journals</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Top-side adjustments over and above what the functions submitted. A separate layer — the P&amp;L is
              always <em>submitted + journals</em>, and the two never merge.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button className={btn} onClick={() => setCreating('')} disabled={busy || creating !== null}>
              <Plus className="w-3.5 h-3.5" /> New journal
            </button>
            <button className={btn} onClick={addLine} disabled={busy || !journal}><Plus className="w-3.5 h-3.5" /> Add line</button>
            <button className={btn} onClick={() => loadDetail(journal?.journal_id)} disabled={!dirty || busy}>
              <Undo2 className="w-3.5 h-3.5" /> Discard
            </button>
            <button className={btnPrimary} onClick={save} disabled={!dirty || busy || !journal}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-3 flex items-start justify-between gap-2 px-3 py-2 rounded-lg border border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10">
            <p className="m-0 text-xs text-red-700 dark:text-red-300">{error}</p>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
        {toast && (
          <div className="mt-3 px-3 py-2 rounded-lg border border-emerald-300 bg-emerald-50 dark:border-emerald-500/40 dark:bg-emerald-500/10">
            <p className="m-0 text-xs text-emerald-700 dark:text-emerald-300">{toast}</p>
          </div>
        )}
      </div>

      {/* ONE SCROLLBAR, AND IT IS THE PAGE'S — same as the P&L. The grid card
          carries NO height constraint, so fitHeight can size the canvas to its
          rows and the lines never scroll inside a box that also scrolls. */}
      <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto space-y-3">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 py-10">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* ---- New journal ------------------------------------------------
                Asked for up front rather than left to be filled in later: the
                reference the server allocates says nothing about what the
                adjustment is, so a journal without a description is one nobody
                can pick out of the selector. */}
            {creating !== null && (
              <div className="rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-3">
                <span className="block mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  New journal · what is this adjustment for?
                </span>
                <div className="flex items-center gap-2">
                  <input
                    autoFocus
                    className="flex-1 min-w-[260px] text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2.5 py-1.5 outline-none focus:ring-2 focus:ring-slate-400"
                    value={creating}
                    placeholder="Central restructuring provision"
                    onChange={(e) => setCreating(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); newJournal(); }
                      if (e.key === 'Escape') { e.preventDefault(); setCreating(null); }
                    }}
                  />
                  <button className={btnPrimary} onClick={newJournal} disabled={busy || !creating.trim()}>
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Create
                  </button>
                  <button className={btn} onClick={() => setCreating(null)} disabled={busy}>Cancel</button>
                </div>
                <p className="m-0 mt-1.5 text-[11px] text-slate-400">
                  The reference (JNL-001, JNL-002…) is allocated for you. This is the line everyone reads.
                </p>
              </div>
            )}

            {/* ---- The journal bar -------------------------------------------
                The selector lives HERE, at the top, not in a column down the
                left. A left list costs 280px of width on every screen for a
                choice made once — and this grid is fifteen columns wide before
                anyone types anything. One dropdown, and the statement gets the
                whole page. */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3">
              <div className="flex flex-wrap items-end gap-4">
                <div className="min-w-[300px]">
                  <span className="block mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Journal · {selectedVersion || 'no version'}
                  </span>
                  <SearchSelect
                    value={selectedId || ''}
                    onChange={(id) => setSelectedId(id || null)}
                    options={journalOptions}
                    placeholder={list.length ? 'Select a journal…' : 'No journals in this version yet'}
                    emptyText="No journal matches."
                    clearable={false}
                    disabled={busy || list.length === 0}
                    ariaLabel="Select journal"
                  />
                </div>

                {journal && (
                  <>
                    <div className="min-w-[240px] flex-1">
                      <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Description</span>
                      <input
                        className="w-full bg-transparent text-sm text-slate-700 dark:text-slate-200 border-b border-slate-200 dark:border-slate-600 hover:border-slate-400 focus:border-slate-500 focus:outline-none py-1"
                        defaultValue={journal.description || ''}
                        key={journal.journal_id}
                        placeholder="What this adjustment is for…"
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v !== (journal.description || '')) saveHeader({ description: v });
                        }}
                      />
                    </div>
                    <div className="text-right">
                      <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Value</span>
                      <span className="font-mono text-sm font-bold text-slate-800 dark:text-slate-100">{fmtR(journalValue)}</span>
                    </div>
                    <div className="text-right">
                      <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Net effect</span>
                      <span className="font-mono text-sm font-bold text-slate-700 dark:text-slate-200">
                        {outOfBalance.length === 0 ? 'Nets to nil' : fmtR(grandTotal)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                      <button className={btn} onClick={removeJournal} disabled={busy} title="Delete this journal">
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Not a warning — a fact. A one-sided adjustment nets to itself
                  and is a perfectly good budget entry; this only exists so a
                  two-sided one that does NOT net out is visible. */}
              {journal && outOfBalance.length > 0 && rows.length > 1 && (
                <p className="m-0 mt-2 flex items-start gap-1.5 text-[11px] text-slate-400">
                  <Scale className="w-3.5 h-3.5 mt-px shrink-0" />
                  <span>
                    This journal does not net to nil in {outOfBalance.map((p) => periodLabel(p, fyStartMonth)).join(', ')} —
                    fine if it is meant to be one-sided, worth a look if the debits and credits were meant to cancel.
                  </span>
                </p>
              )}
              {journal && dirty && !busy && (
                <p className="m-0 mt-2 text-[11px] text-amber-600 dark:text-amber-400">
                  Unsaved changes — nothing reaches the P&L until you press Save.
                </p>
              )}
            </div>

            {/* ---- The lines -------------------------------------------------- */}
            {!journal ? (
              <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 p-8 text-center">
                <p className="m-0 text-sm text-slate-500 dark:text-slate-400">
                  {list.length
                    ? 'Pick a journal above to see its lines.'
                    : 'No journals in this version yet. New journal starts one — a reference is allocated for you.'}
                </p>
              </div>
            ) : (
              <>
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col overflow-hidden">
                  {rows.length === 0 ? (
                    <p className="m-0 px-2 py-8 text-center text-xs text-slate-400">
                      No lines yet. <strong>Add line</strong> — then type the total and it spreads evenly across the year,
                      or type the months directly.
                    </p>
                  ) : (
                    <MantleDataGrid
                      gridId="finance-journal-lines"
                      fitHeight
                      fitHeightPadding={6}
                      columns={gcols}
                      rowCount={rows.length + 1}
                      getCellContent={getCellContent}
                      onCellEdited={onCellEdited}
                      onCellClicked={onCellClicked}
                      onHeaderClicked={(col) => periodHeaderToggle(drill, colIds[col])}
                      freezeColumns={1}
                      hint="Click Full Year → quarters → months to drill into the periods. Debits positive, credits negative; the foot row shows the net effect per month. Click Account, Cost Centre or Site to pick. Type a Total to spread it evenly across the year. ✕ removes a line. Nothing is written until you press Save."
                      toolbar={<>
                        <span className="text-[11px] text-slate-500 dark:text-slate-400">
                          {rows.length} line{rows.length === 1 ? '' : 's'}
                          {dirty && ' · unsaved changes'}
                        </span>
                        <button type="button" className={btn}
                                onClick={drill.yearExpanded ? drill.collapseAll : drill.expandAll}>
                          {drill.yearExpanded ? 'Collapse periods' : 'Expand periods'}
                        </button>
                      </>}
                    />
                  )}
                </div>

                <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
                  A journal <strong>never writes into module data</strong>. It is its own layer with its own tables, and it
                  reaches the P&amp;L as <em>MOD_7_FIN</em> — so &ldquo;what the business submitted&rdquo; and &ldquo;what
                  finance overlaid&rdquo; can always be told apart. Leave <strong>Site</strong> empty for a group-level
                  adjustment belonging to no depot; name a site and it appears on that site&rsquo;s own P&amp;L.
                  There is no posting step: a saved line is on the statement, and a deleted one is off it.
                </p>
              </>
            )}
          </>
        )}
      </div>

      {picker && (
        <PickerPopover
          anchor={{ x: picker.x, y: picker.y }}
          value={rows[picker.row]?.[picker.field] || ''}
          options={pickerOptions}
          placeholder="Search…"
          emptyText="Nothing matches."
          onPick={applyPick}
          onClear={() => applyPick(null)}
          clearLabel={picker.field === 'site_code' ? 'Group (no site)' : '— none —'}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
}
